import React, { useState } from "react"
import {
  Flex, Stack, Box, Image, Heading,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import Button from "components/Buttons/Button"
import { asMoney } from "utilities/strings"
import * as Yup from "yup"
import { EMAIL_VALIDATION_REGEX } from "utilities/constants"
import { yupResolver } from "@hookform/resolvers/yup"
import TOULinks from "components/TOULinks"
import {
  Route, Switch, useHistory, useLocation, useParams,
} from "react-router-dom"
import { checkLeadRetrievalUserEmail, createLeadRetrievalUser } from "api/events"
import {
  LeadRetrievalUser,
  PublicDioboxEvent,
} from "sharedTypes"
import { leadRetrievalRegisterPath } from "utilities/routes"
import useSearchParams from "services/useSearchParams"
import SegmentTitle from "./SegmentTitle"
import EmailScreen from "./Steps/EmailScreen"
import PersonalDataScreen from "./Steps/PersonalDataScreen"
import AlreadyHaveLicenseScreen from "./Steps/AlreadyHaveLicenseScreen"
import BuyLicenseScreen from "./Steps/BuyLicenseScreen"
import PaymentSuccessScreen from "./Steps/PaymentSuccessScreen"

export enum Step {
  PersonalData = "personal-data",
  AlreadyHaveLicense = "already-have-license",
  BuyLicense = "buy-license",
  PaymentSuccess = "payment-success",
}

type Props = {
  event: PublicDioboxEvent
}

const LeadRetrievalRegister = ({ event }: Props) => {
  const { subpath } = useParams<{ subpath: Step }>()
  const [leadRetrievalUser, setLeadRetrievalUser] = useState<LeadRetrievalUser | null>(null)

  const schema = Yup.lazy(() => {
    if (!subpath) {
      return Yup.object().shape({
        email: Yup.string()
          .matches(EMAIL_VALIDATION_REGEX, "Invalid email format")
          .required("Email is required"),
        name: Yup.string().nullable(),
        password: Yup.string().nullable(),
      })
    }

    if (subpath === Step.BuyLicense) {
      return Yup.object().shape({
        email: Yup.string()
          .matches(EMAIL_VALIDATION_REGEX, "Invalid email format")
          .required("Email is required"),
        name: Yup.string().required(),
        password: Yup.string().nullable(),
      })
    }

    return Yup.object().shape({
      email: Yup.string()
        .matches(EMAIL_VALIDATION_REGEX, "Invalid email format")
        .nullable(),
      name: Yup.string().required("Name is required"),
      password: Yup.string()
        .min(12, "Password must be at least 12 characters")
        .required("Password is required"),
    })
  })

  const { email } = useSearchParams()
  const {
    control, handleSubmit, formState: { isValid, isSubmitting }, getValues,
  } = useForm({
    defaultValues: {
      email: email || "", name: "", password: "",
    },
    resolver: yupResolver(schema),
  })
  const location = useLocation()
  const history = useHistory()
  const emailAddress = getValues("email") || email

  const {
    id: eventId,
    eventImageUrl,
    name,
    dateTimeLocation,
    leadRetrievalLicenseFee,
    showEventImage,
    eventImageScale,
  } = event
  const registrationBaseUrl = `${leadRetrievalRegisterPath()}/${eventId}`
  const hasEventImage = showEventImage && eventImageUrl
  const eventImageScaleWidth = 1332

  const handleClickOrEnterKey = handleSubmit(async (values) => {
    switch (subpath) {
      case undefined: {
        const { email: userEmail } = values as { email: string }
        const {
          data: { existingLicense, user },
        } = await checkLeadRetrievalUserEmail(eventId, userEmail)

        if (existingLicense && user) {
          setLeadRetrievalUser(user)
          history.push(`${registrationBaseUrl}/${Step.AlreadyHaveLicense}`)

          return null
        }

        if (user) {
          setLeadRetrievalUser(user)
          history.push(`${registrationBaseUrl}/${Step.BuyLicense}`)
        } else {
          history.push(`${registrationBaseUrl}/${Step.PersonalData}`)
        }

        return null
      }
      case Step.PersonalData: {
        const { data } = await createLeadRetrievalUser(
          eventId, values as LeadRetrievalUser,
        )
        window.location.href = data.url

        return null
      }
      case Step.BuyLicense: {
        if (!leadRetrievalUser || !values.name) {
          return null
        }

        const { data } = await createLeadRetrievalUser(eventId,
          { id: leadRetrievalUser.id, name: values.name })
        window.location.href = data.url

        return null
      }
      default:
        return null
    }
  })

  const handleKeyDown = (keyboardEvent: React.KeyboardEvent) => {
    if (keyboardEvent.key === "Enter" && isValid) {
      keyboardEvent.preventDefault()
      handleClickOrEnterKey()
    }
  }

  const licenseFeeText = `Purchase & Activate License \u2014 ${asMoney(Number(leadRetrievalLicenseFee), "USD", true)}`

  let submitButtonText
  switch (subpath) {
    case undefined: {
      submitButtonText = "Continue"
      break
    }
    case Step.PersonalData: {
      submitButtonText = licenseFeeText
      break
    }
    case Step.AlreadyHaveLicense: {
      submitButtonText = licenseFeeText
      break
    }
    case Step.BuyLicense: {
      submitButtonText = licenseFeeText
      break
    }
    case Step.PaymentSuccess: {
      submitButtonText = licenseFeeText
      break
    }
    default:
  }

  const path = `${leadRetrievalRegisterPath()}/:eventId`

  return (
    <Flex direction="column" bg="gray.50" h="100dvh" overflowY="scroll">
      <Flex my={12} flexDirection="column" alignItems="center" gap={4}>
        <Stack spacing={6} borderRadius="lg" bg="white" borderWidth={2} maxW={670} p={8} mx={4} overflow="hidden">
          <Flex flexDirection="column" alignItems="center" mx={-8} mt={-8}>
            {hasEventImage && (
            <Image
              src={`${eventImageUrl}?fit=crop&w=${eventImageScaleWidth * (eventImageScale / 100)}`}
              w={`${eventImageScale}%`}
              borderTopLeftRadius="lg"
              borderTopRightRadius="lg"
              alt={name}
            />
            )}
            <Heading mt={4} size="lg">{name}</Heading>
            <Box
              textAlign="center"
              dangerouslySetInnerHTML={{ __html: dateTimeLocation }}
              mb={3}
              fontSize="lg"
            />
          </Flex>
          <SegmentTitle leftText="Lead Retrieval App" />
          <Switch location={location}>
            <Route
              path={`${path}/${Step.PersonalData}`}
              render={() => (
                <PersonalDataScreen
                  control={control}
                  emailAddress={emailAddress}
                />
              )}
            />
            <Route
              path={`${path}/${Step.AlreadyHaveLicense}`}
              render={() => {
                if (!leadRetrievalUser) {
                  history.replace(registrationBaseUrl)

                  return null
                }

                return <AlreadyHaveLicenseScreen leadRetrievalUser={leadRetrievalUser} />
              }}
            />
            <Route
              path={`${path}/${Step.BuyLicense}`}
              render={() => {
                if (!leadRetrievalUser) {
                  history.replace(registrationBaseUrl)

                  return null
                }

                return (
                  <BuyLicenseScreen
                    control={control}
                    leadRetrievalUser={leadRetrievalUser}
                  />
                )
              }}
            />
            <Route
              path={`${path}/${Step.PaymentSuccess}`}
              render={() => (
                <PaymentSuccessScreen email={emailAddress} />
              )}
            />
            <Route
              path={path}
            >
              <EmailScreen control={control} onKeyDown={handleKeyDown} />
            </Route>
          </Switch>
        </Stack>
        {![Step.AlreadyHaveLicense, Step.PaymentSuccess].includes(subpath) && (
        <Button
          isDisabled={!isValid}
          onClick={handleClickOrEnterKey}
          isLoading={isSubmitting}
        >{submitButtonText}
        </Button>
        )}
        {[Step.PersonalData, Step.BuyLicense].includes(subpath) && (
        <TOULinks />
        )}
      </Flex>
    </Flex>
  )
}

export default LeadRetrievalRegister
